import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Input,
  InputGroup,
  InputLeftAddon,
  Stack,
  Textarea,
  useToast,
  Icon,
  Link,
  Select,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { editPedidoSchema, formatCurrency, formatQuantidade, formatQuantidadeToNumber } from '../validationSchema'
import { api } from '../../../../../services/api'
import { FiExternalLink } from 'react-icons/fi'
import { useProdutos } from '../hooks/useProdutos'
import { ColorSelector } from './ColorSelector'
import { valorService } from '../services/valorService'
import { prazoService } from '../services/prazoService'
import { useDebounce } from '../../../../../hooks/useDebounce'

export function EditPedidoForm({ isOpen, pedido, onClose, onSuccess }) {
  const toast = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [calculatedSubtotal, setCalculatedSubtotal] = useState('')
  const [coresDisponiveis, setCoresDisponiveis] = useState([])
  const [gravacoesDisponiveis, setGravacoesDisponiveis] = useState([])
  const [isCalculating, setIsCalculating] = useState(false)
  const [isLoadingPrazo, setIsLoadingPrazo] = useState(false)
  const lastRequestRef = useRef('')
  const { data: produtos, isLoading: isLoadingProdutos, error: errorProdutos } = useProdutos()

  const {
    handleSubmit,
    register,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(editPedidoSchema),
    defaultValues: {
      id_pedido: pedido?.id || 0,
      nome_produto: pedido?.produto || '',
      cor_produto: pedido?.cor || '',
      gravacao: pedido?.gravacao || '',
      quantidade: pedido?.quantidade || '',
      valor_unitario: pedido?.valorUnitario?.replace('R$ ', '') || '',
      observacao: pedido?.observacao || '',
      prazo_producao: pedido?.prazoProducao?.replace(' dias úteis', '') || '10',
      codigo_produto: pedido?.codigoProduto || '',
      link_logo: pedido?.logo || '',
      link_pdf: pedido?.layout || '',
      impressao_id: null,
      sub_total: pedido?.subtotal?.replace('R$ ', '') || '',
    },
  })

  const produtoNome = watch('nome_produto')
  const quantidade = watch('quantidade')
  const gravacao = watch('gravacao')
  const debouncedQuantidade = useDebounce(quantidade, 500)
  const logoLink = watch('link_logo')
  const layoutLink = watch('link_pdf')
  const impressaoId = watch('impressao_id')
  const subtotal = watch('sub_total')

  const buscarValorProduto = useCallback(async () => {
    if (!debouncedQuantidade || !impressaoId) return

    const qtdFormatada = formatQuantidadeToNumber(debouncedQuantidade)
    if (qtdFormatada <= 0) return

    const requestSignature = `${qtdFormatada}-${impressaoId}`

    if (isCalculating || requestSignature === lastRequestRef.current) return

    lastRequestRef.current = requestSignature
    setIsCalculating(true)
    setCalculatedSubtotal('')

    try {
      const { valor_unitario, sub_total } = await valorService.buscarValorProduto(qtdFormatada, impressaoId)

      setValue('valor_unitario', valor_unitario.toString().replace('.', ','), { shouldValidate: true })
      // Garante que sub_total seja uma string com formato correto
      setValue('sub_total', sub_total.toString().replace('.', ','), { shouldValidate: true })
      setCalculatedSubtotal(formatCurrency(sub_total))
    } catch (error) {
      console.error('Erro ao buscar valor do produto:', error)
      setCalculatedSubtotal('Erro ao calcular valor')
    } finally {
      setIsCalculating(false)
    }
  }, [debouncedQuantidade, impressaoId, setValue, isCalculating])

  const buscarPrazo = useCallback(async () => {
    if (!debouncedQuantidade) return

    const qtdFormatada = formatQuantidadeToNumber(debouncedQuantidade)
    if (qtdFormatada <= 0) return

    setIsLoadingPrazo(true)
    try {
      const { prazo } = await prazoService.buscarPrazo(qtdFormatada)
      setValue('prazo_producao', typeof prazo === 'number' ? prazo.toString() : prazo, { shouldValidate: true })
    } catch (error) {
      console.error('Erro ao buscar prazo:', error)
    } finally {
      setIsLoadingPrazo(false)
    }
  }, [debouncedQuantidade, setValue])

  const handleProdutoChange = useCallback(
    (produtoNome) => {
      if (!produtos) return

      const produtoSelecionado = produtos.find((p) => p.nome_produto === produtoNome)

      if (produtoSelecionado) {
        setCoresDisponiveis(produtoSelecionado.cores || [])
        setGravacoesDisponiveis(produtoSelecionado.gravacoes || [])

        setValue('nome_produto', produtoSelecionado.nome_produto, { shouldValidate: true })
        setValue('codigo_produto', produtoSelecionado.id_item.toString(), { shouldValidate: true })

        if (produtoSelecionado.gravacoes && produtoSelecionado.gravacoes.length > 0) {
          setValue('gravacao', produtoSelecionado.gravacoes[0].gravacao, { shouldValidate: true })
          setValue('impressao_id', produtoSelecionado.gravacoes[0].id_impressao, { shouldValidate: true })

          setTimeout(() => buscarValorProduto(), 100)
        }

        if (produtoSelecionado.cores && produtoSelecionado.cores.length > 0) {
          const corAtual = watch('cor_produto')
          const corExiste = produtoSelecionado.cores.some((c) => c.nome_cor === corAtual)

          if (!corAtual || !corExiste) {
            setValue('cor_produto', produtoSelecionado.cores[0].nome_cor, { shouldValidate: true })
          }
        }
      }
    },
    [produtos, setValue, watch, buscarValorProduto]
  )

  useEffect(() => {
    if (pedido && isOpen) {
      reset({
        id_pedido: pedido?.id || 0,
        nome_produto: pedido?.produto || '',
        cor_produto: pedido?.cor || '',
        gravacao: pedido?.gravacao || '',
        quantidade: pedido?.quantidade || '',
        valor_unitario: pedido?.valorUnitario?.replace('R$ ', '') || '',
        observacao: pedido?.observacao || '',
        prazo_producao: pedido?.prazoProducao?.replace(' dias úteis', '') || '10',
        codigo_produto: pedido?.codigoProduto || '',
        link_logo: pedido?.logo || '',
        link_pdf: pedido?.layout || '',
        impressao_id: null,
        sub_total: pedido?.subtotal?.replace('R$ ', '') || '',
      })
      setCalculatedSubtotal(pedido?.subtotal || '')
    }
  }, [pedido, isOpen, reset])

  useEffect(() => {
    if (produtos && produtos.length > 0 && produtoNome) {
      const produtoSelecionado = produtos.find((p) => p.nome_produto === produtoNome)
      if (produtoSelecionado) {
        setCoresDisponiveis(produtoSelecionado.cores || [])
        setGravacoesDisponiveis(produtoSelecionado.gravacoes || [])

        const gravacaoAtual = watch('gravacao')
        const gravacaoEncontrada = produtoSelecionado.gravacoes.find((g) => g.gravacao === gravacaoAtual)

        if (gravacaoEncontrada) {
          setValue('impressao_id', gravacaoEncontrada.id_impressao, { shouldValidate: true })
        } else if (produtoSelecionado.gravacoes.length > 0) {
          setValue('gravacao', produtoSelecionado.gravacoes[0].gravacao, { shouldValidate: true })
          setValue('impressao_id', produtoSelecionado.gravacoes[0].id_impressao, { shouldValidate: true })
        }
      }
    }
  }, [produtos, produtoNome, setValue, watch])

  useEffect(() => {
    if (debouncedQuantidade && impressaoId) {
      buscarValorProduto()
      buscarPrazo()
    }
  }, [debouncedQuantidade, impressaoId, buscarValorProduto, buscarPrazo])

  useEffect(() => {
    if (gravacao && gravacoesDisponiveis.length > 0) {
      const gravacaoSelecionada = gravacoesDisponiveis.find((g) => g.gravacao === gravacao)
      if (gravacaoSelecionada) {
        setValue('impressao_id', gravacaoSelecionada.id_impressao, { shouldValidate: true })

        setTimeout(() => buscarValorProduto(), 100)
      }
    }
  }, [gravacao, gravacoesDisponiveis, setValue, buscarValorProduto])

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      await api.put('/site/v2/tercerizado/edita-pedido-tercerizado', data)

      toast({
        title: 'Pedido atualizado',
        description: 'As informações do pedido foram atualizadas com sucesso.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      onSuccess()
      onClose()
    } catch (error) {
      console.error('Erro ao atualizar pedido:', error)
      toast({
        title: 'Erro ao atualizar',
        description: error.response?.data?.message || 'Ocorreu um erro ao atualizar o pedido.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleQuantidadeChange = (e) => {
    const rawValue = e.target.value.replace(/\./g, '').replace(/\D/g, '')
    const formattedValue = formatQuantidade(rawValue)
    setValue('quantidade', formattedValue)
  }

  // Método aprimorado para tratar a entrada de subtotal
  const handleSubtotalChange = (e) => {
    const rawValue = e.target.value
      .replace(/R\$\s?/g, '')
      .replace(/\./g, '')
      .replace(/[^\d,]/g, '')

    // Atualiza o campo no formulário como string
    setValue('sub_total', rawValue, { shouldValidate: true })

    // Atualiza o estado visual
    setCalculatedSubtotal(formatCurrency(rawValue.replace(',', '.')))
  }

  const handleColorChange = (field, value) => {
    setValue(field, value, { shouldValidate: true })
  }

  return (
    <Collapse in={isOpen} animateOpacity>
      <Box as="form" onSubmit={handleSubmit(onSubmit)} p={4} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200" mt={2} mb={2}>
        <Grid templateColumns="repeat(12, 1fr)" gap={4}>
          <GridItem colSpan={{ base: 12, md: 6, lg: 3 }}>
            <FormControl isInvalid={errors.nome_produto}>
              <FormLabel fontSize="xs" mb={1}>
                Produto
              </FormLabel>
              {isLoadingProdutos ? (
                <Spinner size="sm" color="#378BBF" />
              ) : errorProdutos ? (
                <Text fontSize="12px" color="red.500">
                  Erro ao carregar produtos
                </Text>
              ) : (
                <Select
                  {...register('nome_produto')}
                  onChange={(e) => handleProdutoChange(e.target.value)}
                  size="sm"
                  fontSize="xs"
                  border="1px"
                  borderColor={errors.nome_produto ? 'red.500' : 'gray.200'}
                  borderRadius="md"
                >
                  {produtos &&
                    produtos.map((produto) => (
                      <option key={produto.id_item} value={produto.nome_produto}>
                        {produto.nome_produto}
                      </option>
                    ))}
                </Select>
              )}
              <FormErrorMessage>{errors.nome_produto?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 12, md: 6, lg: 3 }}>
            <FormControl isInvalid={errors.cor_produto}>
              <FormLabel fontSize="xs" mb={1}>
                Cor
              </FormLabel>
              <Input type="hidden" {...register('cor_produto')} />
              <ColorSelector
                value={watch('cor_produto')}
                onChange={handleColorChange}
                options={coresDisponiveis}
                //isDisabled={coresDisponiveis.length === 0 || isLoadingProdutos}
              />
              <FormErrorMessage>{errors.cor_produto?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 12, md: 6, lg: 3 }}>
            <FormControl isInvalid={errors.gravacao}>
              <FormLabel fontSize="xs" mb={1}>
                Gravação
              </FormLabel>
              <Controller
                name="gravacao"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    size="sm"
                    fontSize="xs"
                    border="1px"
                    borderColor={errors.gravacao ? 'red.500' : 'gray.200'}
                    borderRadius="md"
                    //isDisabled={gravacoesDisponiveis.length === 0}
                  >
                    {gravacoesDisponiveis.map((item) => (
                      <option key={item.id_impressao} value={item.gravacao}>
                        {item.gravacao}
                      </option>
                    ))}
                  </Select>
                )}
              />
              <FormErrorMessage>{errors.gravacao?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 12, md: 6, lg: 3 }}>
            <FormControl isInvalid={errors.codigo_produto}>
              <FormLabel fontSize="xs" mb={1}>
                Código do Produto
              </FormLabel>
              <Input
                {...register('codigo_produto')}
                size="sm"
                fontSize="xs"
                //isReadOnly
                bg="gray.100"
                placeholder="Código do Produto"
              />
              <FormErrorMessage>{errors.codigo_produto?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 12, md: 4, lg: 2 }}>
            <FormControl isInvalid={errors.quantidade}>
              <FormLabel fontSize="xs" mb={1}>
                Quantidade
              </FormLabel>
              <Input
                {...register('quantidade')}
                onChange={handleQuantidadeChange}
                textAlign="right"
                size="sm"
                fontSize="xs"
                placeholder="Quantidade"
              />
              <FormErrorMessage>{errors.quantidade?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 12, md: 4, lg: 2 }}>
            <FormControl isInvalid={errors.valor_unitario}>
              <FormLabel fontSize="xs" mb={1}>
                Valor Unitário
              </FormLabel>
              <InputGroup size="sm">
                <InputLeftAddon fontSize="xs">R$</InputLeftAddon>
                <Input
                  {...register('valor_unitario')}
                  size="sm"
                  fontSize="xs"
                  textAlign="right"
                  //isReadyOnly
                  bg="gray.100"
                />
              </InputGroup>
              <FormErrorMessage>{errors.valor_unitario?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 12, md: 4, lg: 2 }}>
            <FormControl isInvalid={errors.sub_total}>
              <FormLabel fontSize="xs" mb={1}>
                Subtotal
              </FormLabel>
              <InputGroup size="sm">
                <InputLeftAddon fontSize="xs">R$</InputLeftAddon>
                <Input
                  {...register('sub_total')}
                  onChange={handleSubtotalChange}
                  textAlign="right"
                  size="sm"
                  fontSize="xs"
                  isReadOnly={isCalculating}
                  bg={isCalculating ? 'gray.100' : 'white'}
                />
              </InputGroup>
              {!isCalculating && calculatedSubtotal === 'Erro ao calcular valor' && (
                <Text fontSize="10px" color="red.500" mt={1}>
                  Falha ao obter valor da API
                </Text>
              )}
              <FormErrorMessage>{errors.sub_total?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 12, md: 6, lg: 6 }}>
            <FormControl isInvalid={errors.link_logo}>
              <FormLabel fontSize="xs" mb={1}>
                Link do Logo
              </FormLabel>
              <InputGroup size="sm">
                <Input {...register('link_logo')} size="sm" fontSize="xs" placeholder="https://exemplo.com/logo.png" />
                {logoLink && (
                  <InputLeftAddon
                    as={Link}
                    href={logoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    bg="blue.500"
                    color="white"
                    _hover={{ bg: 'blue.600' }}
                  >
                    <Icon as={FiExternalLink} />
                  </InputLeftAddon>
                )}
              </InputGroup>
              <FormErrorMessage>{errors.link_logo?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 12, md: 6, lg: 6 }}>
            <FormControl isInvalid={errors.link_pdf}>
              <FormLabel fontSize="xs" mb={1}>
                Link do Layout
              </FormLabel>
              <InputGroup size="sm">
                <Input {...register('link_pdf')} size="sm" fontSize="xs" />
                {layoutLink && (
                  <InputLeftAddon
                    as={Link}
                    href={layoutLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    bg="blue.500"
                    color="white"
                    _hover={{ bg: 'blue.600' }}
                  >
                    <Icon as={FiExternalLink} />
                  </InputLeftAddon>
                )}
              </InputGroup>
              <FormErrorMessage>{errors.link_pdf?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 12, md: 6, lg: 3 }}>
            <FormControl isInvalid={errors.prazo_producao}>
              <FormLabel fontSize="xs" mb={1}>
                Prazo de Produção
              </FormLabel>
              <InputGroup size="sm">
                <Input
                  {...register('prazo_producao')}
                  size="sm"
                  fontSize="xs"
                  // isReadOnly={isLoadingPrazo}
                  bg={isLoadingPrazo ? 'gray.100' : 'white'}
                />
                {isLoadingPrazo && (
                  <InputLeftAddon p={0} bg="transparent" border="none">
                    <Spinner size="xs" color="blue.500" mr={2} />
                  </InputLeftAddon>
                )}
              </InputGroup>
              <FormErrorMessage>{errors.prazo_producao?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 12, md: 6, lg: 3 }}>
            <FormControl isInvalid={errors.data_entrada}>
              <FormLabel fontSize="xs" mb={1}>
                Data de Entrada
              </FormLabel>
              <Input {...register('data_entrada')} type="date" size="sm" fontSize="xs" />
              <FormErrorMessage>{errors.data_entrada?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem colSpan={12}>
            <FormControl isInvalid={errors.observacao}>
              <FormLabel fontSize="xs" mb={1}>
                Observação
              </FormLabel>
              <Textarea {...register('observacao')} size="sm" fontSize="xs" rows={2} />
              <FormErrorMessage>{errors.observacao?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>

        <Stack direction="row" spacing={4} mt={4} justifyContent="flex-end">
          <Button onClick={onClose} size="sm" variant="outline" colorScheme="gray" fontSize="xs">
            Cancelar
          </Button>
          <Button
            type="submit"
            size="sm"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText="Salvando"
            fontSize="xs" //isDisabled={isCalculating}
          >
            Salvar Alterações
          </Button>
        </Stack>
      </Box>
    </Collapse>
  )
}
